import React from "react";
import Icon from "@mdi/react";
import { mdiCalendarMonth } from "@mdi/js";
import { Spin } from "antd";
import { currencyDisplay } from "../../../../utils/helpers";

export type SummaryRowKey = "date" | "travellers" | "room" | "language" | "partner";

interface Props {
  title: string;
  departureDate: string;
  travellers: string;
  room?: string;
  language?: string;
  partner?: string;
  total: number | undefined;
  /** Credit/debit card admin fee already included in `total`, shown as its own line. */
  adminFee?: number;
  ccRate?: number;
  loading?: boolean;
  onShowBreakdown: () => void;
  /** Present on the partner and payment steps, where each row can be changed. */
  onEdit?: (row: SummaryRowKey) => void;
}

// The sticky "Package" card on the right of every checkout step (Figma 10123:274603).
export default function BookingSummaryCard({
  title, departureDate, travellers, room, language, partner, total, adminFee = 0, ccRate = 0, loading, onShowBreakdown, onEdit,
}: Props) {
  const row = (key: SummaryRowKey, label: string, value: React.ReactNode, large = false) => (
    <div className="gt-summary__row">
      <div>
        <span className="gt-summary__label">{label}</span>
        <strong className={large ? "gt-summary__value is-large" : "gt-summary__value"}>{value}</strong>
      </div>
      {onEdit && <button type="button" className="gt-edit-btn" onClick={() => onEdit(key)}>Edit</button>}
    </div>
  );

  return (
    <aside className="gt-summary">
      <h2 className="gt-summary__title">{title}</h2>
      {row("date", "Departure Date", <><Icon path={mdiCalendarMonth} size="22px" color="#dc1a22" />{departureDate}</>, true)}
      {row("travellers", "Travellers", travellers)}
      {room && row("room", "Room Selection", room)}
      {language && row("language", "Language", language)}
      {partner && row("partner", "Preferred Partner", partner)}

      {adminFee > 0 && (
        <div className="gt-summary__row gt-summary__fee">
          <div><span className="gt-summary__label">Admin fee ({Number((ccRate * 100).toFixed(2))}%)</span><strong className="gt-summary__value">{currencyDisplay("MYR", adminFee)}</strong></div>
        </div>
      )}

      <div className="gt-summary__total">
        <div>
          <span className="gt-summary__total-label">Total</span>
          <small>(incl. taxes & fees)</small>
        </div>
        <div className="gt-summary__total-amount">
          <strong>{loading || total === undefined ? <Spin size="small" /> : currencyDisplay("MYR", total)}</strong>
          <button type="button" onClick={onShowBreakdown}>Price breakdown</button>
        </div>
      </div>
    </aside>
  );
}
